import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { getNewsItems, type NewsItem } from "../utils/adminStore";
import { PageHero } from "../components/PageHero";
import { injectStructuredData, getNewsArticleSchema } from "../utils/seo";

interface NewsPageProps {
  slug?: string;
}

// Inline Markdown Parser for bold, italic, code, and links
function parseInlineMarkdown(text: string): React.ReactNode[] {
  const regex = /(\*\*.*?\*\*|\*.*?\*|`.*?`|\[.*?\]\(.*?\))/g;
  const parts = text.split(regex);

  return parts.map((part, i) => {
    if (!part) return null;
    if (part.startsWith("**") && part.endsWith("**") && part.length >= 4) {
      return (
        <strong key={i} className="font-bold text-slate-900 dark:text-white">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith("*") && part.endsWith("*") && part.length >= 2) {
      return (
        <em key={i} className="italic text-slate-800 dark:text-slate-200">
          {part.slice(1, -1)}
        </em>
      );
    }
    if (part.startsWith("`") && part.endsWith("`") && part.length >= 2) {
      return (
        <code
          key={i}
          className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-brand dark:text-sky-300 font-mono text-xs font-semibold"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    const linkMatch = part.match(/^\[(.*?)\]\((.*?)\)$/);
    if (linkMatch) {
      return (
        <a
          key={i}
          href={linkMatch[2]}
          className="text-brand dark:text-primary underline font-medium hover:opacity-80 transition-opacity"
        >
          {linkMatch[1]}
        </a>
      );
    }
    return part;
  });
}

export function NewsPage({ slug }: NewsPageProps) {
  const [newsList, setNewsList] = useState<NewsItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    const sync = () => setNewsList(getNewsItems());
    sync();
    window.addEventListener("radiece_admin_update", sync);
    return () => window.removeEventListener("radiece_admin_update", sync);
  }, []);

  const singleItem = useMemo(() => {
    if (!slug) return null;
    return newsList.find((n) => n.slug === slug || n.id === slug);
  }, [slug, newsList]);

  useEffect(() => {
    if (singleItem) {
      document.title = `${singleItem.title} | RADIECE News`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute("content", singleItem.summary);

      injectStructuredData(
        getNewsArticleSchema({
          title: singleItem.title,
          summary: singleItem.summary,
          slug: singleItem.slug,
          publishedAt: singleItem.publishedAt,
          author: singleItem.author,
        })
      );
    }
  }, [singleItem]);

  const categories = ["All", "Platform Update", "Engineering Dispatch", "Company Release", "Statutory Notice"];

  const filteredNews = useMemo(() => {
    return newsList.filter(
      (n) => selectedCategory === "All" || n.category === selectedCategory
    );
  }, [newsList, selectedCategory]);

  if (singleItem) {
    return (
      <div className="bg-white dark:bg-[#090e17] py-12 sm:py-16 transition-colors">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <a
              href="/news"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-brand dark:text-primary hover:underline uppercase tracking-wider"
            >
              ← Back to All News & Dispatches
            </a>
          </div>

          <header className="mb-8 pb-6 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-3 py-1 bg-primary/10 border border-primary/20 text-brand dark:text-primary text-xs font-bold rounded-full uppercase tracking-wider">
                {singleItem.category}
              </span>
              <span className="text-xs font-mono text-slate-400">
                {singleItem.publishedAt}
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-brand-dark dark:text-slate-100 tracking-tight leading-snug">
              {singleItem.title}
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
              Issued by: <strong className="text-slate-700 dark:text-slate-200">{singleItem.author}</strong>
            </p>
          </header>

          <main className="max-w-none text-sm sm:text-base leading-relaxed text-slate-700 dark:text-slate-300 space-y-4 font-light">
            {singleItem.content.split("\n").map((line, i) => {
              if (line.startsWith("### ")) {
                return (
                  <h3 key={i} className="text-lg font-bold text-brand-dark dark:text-slate-100 mt-6 mb-2">
                    {parseInlineMarkdown(line.replace("### ", ""))}
                  </h3>
                );
              }
              if (/^\d+\.\s+/.test(line)) {
                return (
                  <div key={i} className="my-2 pl-3 border-l-2 border-primary text-sm text-slate-700 dark:text-slate-300">
                    {parseInlineMarkdown(line)}
                  </div>
                );
              }
              if (line.trim().length > 0) {
                return <p key={i} className="my-3 leading-relaxed">{parseInlineMarkdown(line)}</p>;
              }
              return null;
            })}
          </main>

          <div className="mt-12 pt-6 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
            <a
              href="/news"
              className="px-5 py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-slate-200 transition-colors"
            >
              ← Back to Timeline
            </a>
            <a
              href="/contact"
              className="px-5 py-2.5 bg-brand dark:bg-primary text-white dark:text-slate-950 rounded-xl text-xs font-bold uppercase tracking-wider hover:opacity-90 transition-opacity"
            >
              Contact Desk →
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHero
        badge="Live Company Dispatches & Announcements"
        title="Newsfeed & Platform Updates"
        description="Official press releases, technology release notes, firmware dispatches, and corporate statutory updates from RADIECE PRIVATE LIMITED."
      />

      <section className="bg-slate-50 dark:bg-[#070b12] py-12 sm:py-16 transition-colors">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter Chips */}
          <div className="flex flex-wrap gap-2 mb-10 justify-center sm:justify-start">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all cursor-pointer ${
                  selectedCategory === cat
                    ? "bg-brand text-white dark:bg-primary dark:text-slate-950 shadow-sm"
                    : "bg-white dark:bg-[#0e1724] text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-800 hover:border-brand/40"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Clean, Perfectly Aligned Chronological Timeline */}
          <div className="space-y-6">
            {filteredNews.map((item, idx) => (
              <motion.article
                key={item.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: idx * 0.05 }}
                className="flex gap-4 sm:gap-6 items-start group"
              >
                {/* Continuous Timeline Spine & Node */}
                <div className="flex flex-col items-center self-stretch shrink-0 pt-6">
                  {/* Glowing Node Dot */}
                  <div className="w-3.5 h-3.5 rounded-full bg-white dark:bg-[#0e1724] border-2 border-brand dark:border-primary group-hover:scale-125 group-hover:bg-brand dark:group-hover:bg-primary transition-all shadow-sm ring-4 ring-primary/10" />
                  
                  {/* Connecting Spine Line */}
                  {idx < filteredNews.length - 1 && (
                    <div className="w-0.5 grow bg-slate-200 dark:bg-slate-800 mt-2 min-h-[3rem]" />
                  )}
                </div>

                {/* News Card */}
                <div className="flex-1 card-interactive bg-white dark:bg-[#0e1724] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-7 shadow-xs hover:border-brand/40 dark:hover:border-primary/40 transition-colors">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-brand dark:text-primary uppercase tracking-wider">
                        {item.category}
                      </span>
                      {item.timelineType === "Milestone" ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20 uppercase tracking-wider">
                          ✓ Corporate Milestone (21-07-2026)
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20 uppercase tracking-wider">
                          ✦ Future Vision & Roadmap
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-slate-400 font-mono">
                      📅 {item.publishedAt}
                    </span>
                  </div>

                  <h2 className="text-base sm:text-lg font-bold text-brand-dark dark:text-slate-100 mb-2 group-hover:text-brand dark:group-hover:text-primary transition-colors leading-snug">
                    <a href={`/news/${item.slug}`}>
                      {item.title}
                    </a>
                  </h2>

                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed font-light mb-4">
                    {item.summary}
                  </p>

                  <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-100 dark:border-slate-800 text-xs">
                    <span className="text-slate-400 text-[11px]">
                      Desk: <strong className="text-slate-600 dark:text-slate-300 font-medium">{item.author}</strong>
                    </span>
                    <a
                      href={`/news/${item.slug}`}
                      className="text-brand dark:text-primary font-bold hover:underline inline-flex items-center gap-1 uppercase tracking-wider text-[11px]"
                    >
                      Read Full Notice →
                    </a>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
